import React, { useMemo } from 'react';
import cn from 'classnames';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { useToggle } from 'apps/sigma2-ui/src/lib/hooks/useToggle';
import { IconChevronRightSvg } from 'apps/sigma2-ui/src/components/svg/IconChevronRight';
import { FilterDefinitionForTable } from 'apps/sigma2-ui/src/explore-section/utils/HeatmapTableTypes';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material';

interface BaseOption {
  value: string;
  label: string;
}

/** Props for the HeatmapMetricBar component */
interface MetricBarProps<T extends BaseOption = FilterDefinitionForTable> {
  /** Currently selected option value */
  selectedValue: T['value'];
  /** Callback fired when a new option is selected */
  onSelect: (value: T['value']) => void;
  /** Available options */
  options: T[];
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Custom label for the metric bar */
  labelTranslatedContent: TranslatedContent;
  /** Custom render function for option items */
  renderOption?: (option: T) => React.ReactNode;
  /** Custom sort function for options */
  sortOptions?: (a: T, b: T) => number;
}

export const MetricBar = <T extends BaseOption>({
  selectedValue,
  onSelect,
  options,
  disabled = false,
  labelTranslatedContent,
  renderOption,
  sortOptions = (a, b) => a.label.localeCompare(b.label),
}: MetricBarProps<T>) => {
  const language = useLanguage();
  const [isOpen, toggleOpen] = useToggle({ initialState: false });

  const sortedOptions = useMemo(
    () => [...options].sort(sortOptions),
    [options, sortOptions],
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <div>
      {sortedOptions.length > 0 && (
        <Spacing direction="horizontal" size="sm" className="align-middle">
          <div className="flex-1">
            <S2Typography
              as="span"
              variant={isMobile ? 'headline-7-alt' : 'headline-6-alt'}
              translatedContent={labelTranslatedContent}
            />
          </div>
          <div className="relative">
            <button
              data-testid="heatmap-metric-bar-button"
              onClick={disabled ? void 0 : toggleOpen}
              className={cn({
                'cursor-not-allowed': disabled,
                'text-royal-600': !disabled,
              })}
            >
              <Spacing direction="horizontal" size="xs">
                <S2Typography
                  as="span"
                  variant={isMobile ? 'headline-7-alt' : 'headline-6-alt'}
                  translatedContent={language.dangerousUserInput({
                    value:
                      sortedOptions.find(
                        (option) => option.value === selectedValue,
                      )?.label || '-',
                    fallback: '-',
                  })}
                  textColorVariant={disabled ? 'gray-400' : 'royal-600'}
                />

                <IconChevronRightSvg
                  svg={{
                    className: cn('w-6 h-6', {
                      'rotate-90': !isOpen,
                      '-rotate-90': isOpen,
                      'text-royal-600': !disabled,
                      'text-gray-400': disabled,
                    }),
                  }}
                />
              </Spacing>
            </button>

            <div
              className={cn('w-full border-t-2 -mt-1', {
                'border-royal-600': !disabled,
                'border-gray-400': disabled,
              })}
            />

            {isOpen && !disabled && (
              <Spacing
                direction="vertical"
                size="gap-0"
                className={
                  'z-50 absolute left-2 top-8 max-h-96 h-fit w-max bg-white shadow-4px min-w-44'
                }
              >
                {sortedOptions.map((option) => {
                  const isSelected = option.value === selectedValue;

                  return (
                    <button
                      data-testid={`heatmap-metric-bar-button-${option.value}`}
                      key={option.value}
                      onClick={() => {
                        onSelect(option.value);
                        toggleOpen();
                      }}
                      className={cn('p-2 text-left w-full', {
                        'bg-royal-100': isSelected,
                        'bg-white hover:bg-royal-100': !isSelected,
                      })}
                    >
                      {renderOption
                        ? renderOption(option)
                        : option.label || option.value}
                    </button>
                  );
                })}
              </Spacing>
            )}
          </div>
        </Spacing>
      )}
    </div>
  );
};
